import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { APIService } from 'app/services/api.service';
import { MenuStorage } from 'app/models/menuStorage.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import { IBaseResponse } from 'app/models/baseResponse.model';
import { utcToLocalFormat } from "app/shared/pipes/utc-to-local.pipe";
import { AppHelper } from "app/helpers/appHelper";
import {
  ProductStoragesModalComponent, ProductStoragesModalComponentData
} from '@location/shared/product-storages-modal/product-storages-modal.component';
import { ObjectStorage, ObjectStorageType } from 'app/models/objectStorage.model';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { selectHasPermission } from 'app/reducers/context.selectors';
import { PermissionCode } from 'app/helpers/permission-codes';

@UntilDestroy()
@Component({
  selector: 'app-menu-history-modal',
  templateUrl: './menu-history-modal.component.html',
  styleUrls: ['./menu-history-modal.component.scss']
})
export class MenuHistoryModalComponent implements OnInit {
  menuHistory: MenuStorage[] = [];
  dataSource = new MatTableDataSource(this.menuHistory);
  columnsToDisplay = ['revision', 'uploadedAt', 'published', 'uploadMessage', 'uploadResult', 'actions', 'id'];
  dateFormat: string = utcToLocalFormat;

  // Права IC-2972: ручная публикация и скачивание файлов конвертации из истории.
  hasPublicationToggle$: Observable<boolean> = this.store.select(selectHasPermission(PermissionCode.MenuPublicationToggle));
  hasConversionFileDownload$: Observable<boolean> = this.store.select(selectHasPermission(PermissionCode.MenuConversionFileDownload));

  constructor(
    private api: APIService,
    private translate: TranslateService,
    public dialogRef: MatDialogRef<MenuHistoryModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private router: Router,
    private store: Store,
  ) {
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.api.getMenuStorageList(this.data.pipeConfigurationId)
      .then(({result, error}) => {
        if (!error) {
          this.menuHistory = [];
          this.menuHistory = [...result];
          this.dataSource = new MatTableDataSource(this.menuHistory);
        } else {
          this.dataSource = new MatTableDataSource([]);
        }
      });
  }

  canGoToInitiatorConfigurations(menuStorage: MenuStorage): boolean {
    return !!menuStorage.initiatorPipeId
      && menuStorage.initiatorPipeId !== this.data.pipeConfigurationId;
  }

  async goToInitiatorConfigurations(menuStorage: MenuStorage): Promise<void> {
    const initiatorPipeId = menuStorage.initiatorPipeId;
    if (!initiatorPipeId || initiatorPipeId === this.data.pipeConfigurationId) {
      return;
    }

    const {result: organizationId, error} = await this.api.getOrganizationIdByPipeId(initiatorPipeId);
    if (error || !organizationId) {
      return;
    }

    this.dialogRef.close();
    await this.router.navigate(['/app', organizationId, 'location', 'configurations']);
  }

  getColumnHeader(columnHeaderValue: string): string {
    switch (columnHeaderValue) {
      case 'revision':
        return this.translate.instant('MENU_HISTORY.REVISION');
      case 'uploadedAt':
        return this.translate.instant('MENU_HISTORY.UPLOADED_AT');
      case 'published':
        return this.translate.instant('MENU_HISTORY.PUBLISHED_AT');
      case 'uploadMessage':
        return this.translate.instant('MENU_HISTORY.UPLOAD_MESSAGE');
      case 'uploadResult':
        return this.translate.instant('MENU_HISTORY.UPLOAD_RESULT');
      case 'actions':
        return this.translate.instant('MENU_HISTORY.ACTIONS');
      default:
        return '';
    }
  }

  downloadBucket(prefix:string, objectId: string, uploadedAt: string): void {
    const name = `${prefix}_${uploadedAt}`;
    this.api.getBucketById(objectId, name)
      .subscribe({
        next: (res: IBaseResponse<string>) => {
          if (res) {
            AppHelper.openList(res.data);
          }
        },
        error: (e) => {
          console.log(e);
          alert('Can\'t download file');
        }
      });
  }

  async confirmPublish(menuStorageId: string) {
    if(!confirm(this.translate.instant('MENU_HISTORY.CONFIRM_PUBLISH'))){
      return;
    }
    const result = await this.api.confirmPublishingMenu(menuStorageId, this.data.pipeConfigurationId);
    this.menuHistory = this.menuHistory.map(mh => {
      if(mh.id == menuStorageId){
        mh.published = result.result;
      }
      return mh;
    })
  }

  async cancelPublish(menuStorageId: string) {
    if(!confirm( this.translate.instant('MENU_HISTORY.CONFIRM_CANCEL_PUBLISH'))){
      return;
    }
    await this.api.cancelPublishingMenu(menuStorageId, this.data.pipeConfigurationId);
    this.menuHistory = this.menuHistory.map(mh => {
      if(mh.id == menuStorageId){
        mh.published = null;
      }
      return mh;
    })
  }

  openProductStoragesModal = (menuStorage: MenuStorage) => {
    const data: ProductStoragesModalComponentData = {
      menuStorage,
    }
    const dialogRef = this.dialog.open(ProductStoragesModalComponent, {
      minWidth: '50%',
      maxWidth: '1100px',
      data,
    });
  }

  getObjectStorageMatTooltip(objectStorage: ObjectStorage) {
    switch (objectStorage.type) {
      case ObjectStorageType.Menu:
        return this.translate.instant('MENU.DOWNLOAD') + ` ${objectStorage.name.split('_')[1]}`;
      case ObjectStorageType.KeyValue:
        return this.translate.instant('MENU.DOWNLOAD_GUID_COMBINATIONS');
      case ObjectStorageType.DerivativeResult:
        return this.translate.instant('MENU.DOWNLOAD_DERIVATIVE');
      case ObjectStorageType.ErrorResult:
        return this.translate.instant('MENU.DOWNLOAD_ERROR');
      default:
        return objectStorage.type;
    }
  }
}
